<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { useSchoolLevelsBySchoolType } from '@/composables/useSchoolLevelsBySchoolType';
import ClassFormFooter, { IClassFormFooterConfig } from '@/core/dialogs/create-class/ClassFormFooter.vue';
import DialogBase from '@/core/dialogs/base-dialog/DialogBase.vue';
import { computed, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { assertNotNullOrUndefined } from '@/utils/assertion.utils';
import SchoolSubjectImportBody from '../template-library/SchoolSubjectImportBody.vue';
import { useSchoolSubject } from '@/composables/schoolSubject/useSchoolSubject';
import { useSchoolSubjectGroup } from '@/composables/schoolSubjectGroups/useSchoolSubjectGroup';
import { fetchCompetenceDomainTemplatesForSchoolLevel } from '@/api/resources/competenceDomain.api';

const { t } = useI18n();
const { allSchoolSubjectTemplates } = useSchoolSubject();
const { allSchoolSubjectGroupTemplates } = useSchoolSubjectGroup();
const { schoolLevelOptions } = useSchoolLevelsBySchoolType();
const { confirm } = useConfirmDialog();
const { getSchoolLevelLabel } = useSchoolLevelsBySchoolType();

const emit = defineEmits<{
  continue: [value: Components.Schemas.SchoolSubjectDto[]];
}>();

defineProps<{
  footerConfig?: IClassFormFooterConfig;
  title: string;
}>();

const dialogRef = useTemplateRef('dialogRef');
const allowSameSchoolLevel = ref<boolean>(false);
const availableCompetenceDomains = ref<Components.Schemas.CompetenceDomainDto[]>([]);

const defaultFooterConfig = {
  nextBtnLabel: t('general.continue'),
  previousBtnLabel: t('general.back'),
  helpText: t('onboarding.schoolSubject.helpText'),
};

const currentSchoolLevel = ref<number | null>(null);
const selectedSchoolLevel = ref<number | null>(null);

const selectedSchoolSubjects = ref<Components.Schemas.SchoolSubjectDto[]>([]);
const availableSchoolSubjects = computed<Components.Schemas.SchoolSubjectDto[]>(() => {
  return allSchoolSubjectTemplates.value.filter(
    (schoolSubject) => schoolSubject.schoolLevel === selectedSchoolLevel.value,
  );
});

const show = (params: { schoolLevel: number; allowSameSchoolLevelImport?: boolean }): void => {
  currentSchoolLevel.value = params.schoolLevel;

  // Check if currentSchoolLevel can be selected, otherwise select the first available option
  if (!schoolLevelOptions.value.some((option) => option.value === currentSchoolLevel.value)) {
    selectedSchoolLevel.value = schoolLevelOptions.value[0]?.value ?? null;
  } else {
    selectedSchoolLevel.value = params.schoolLevel;
  }

  allowSameSchoolLevel.value = params.allowSameSchoolLevelImport ?? false;
  dialogRef.value?.show();
};

const updateCompetenceDomains = async (): Promise<void> => {
  if (selectedSchoolLevel.value !== null)
    availableCompetenceDomains.value = await fetchCompetenceDomainTemplatesForSchoolLevel(selectedSchoolLevel.value);
};

const hide = (): void => {
  selectedSchoolLevel.value = null;
  selectedSchoolSubjects.value = [];
  currentSchoolLevel.value = null;
  dialogRef.value?.hide();
};

const onSubmit = async (): Promise<void> => {
  assertNotNullOrUndefined(selectedSchoolLevel.value);
  assertNotNullOrUndefined(currentSchoolLevel.value);

  if (selectedSchoolLevel.value !== currentSchoolLevel.value) {
    const confirmed = await confirm({
      title: t('schoolSubjects.confirm.title'),
      description: t('schoolSubjects.confirm.description', {
        sourceLabel: getSchoolLevelLabel(selectedSchoolLevel.value),
        targetLabel: getSchoolLevelLabel(currentSchoolLevel.value),
      }),
      confirmButtonLabel: t('schoolSubjects.confirm.confirmButtonLabel'),
      declineButtonLabel: t('general.cancel'),
      theme: 'primary',
    });

    if (!confirmed) return;
  }

  emit('continue', selectedSchoolSubjects.value);
  hide();
};

watch(selectedSchoolLevel, updateCompetenceDomains, { immediate: true });

defineExpose({
  show,
});
</script>

<template>
  <DialogBase ref="dialogRef" :title="title" fill-height no-padding>
    <SchoolSubjectImportBody
      v-model:selected-school-level="selectedSchoolLevel"
      v-model:selected-school-subjects="selectedSchoolSubjects"
      :school-level-options="schoolLevelOptions"
      :available-school-subjects="availableSchoolSubjects"
      :available-school-subject-groups="allSchoolSubjectGroupTemplates"
      :available-competence-domains="availableCompetenceDomains"
      :no-subjects-message="t('schoolSubjects.noSubjects')"
    />
    <template #footer>
      <ClassFormFooter
        :footer-config="footerConfig ?? defaultFooterConfig"
        form-valid
        button-type="button"
        @click:next="onSubmit"
        @click:previous="hide"
      />
    </template>
  </DialogBase>
</template>
